import React, { ComponentProps } from 'react'
import { Spin } from 'antd'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { useCalculationTaskPrevalenceValues } from '../../../../api/benchmarking'
import BenchmarkingContentRow from '../../components/BenchmarkingContentRow'
import { baseGraphOptions } from '../../util/baseGraphOptions'
import { getColorByIndex } from '../../util/colors'
import { getFontSize } from '../../util/fontSize'
import { BORDER_WIDTH } from '../../util/constants'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const TITLE = 'Insatsprevalens'

export const CalculationTaskPrevalence: React.FC<{ text: React.ReactNode }> = ({ text }) => {
  const { data, isLoading, error } = useCalculationTaskPrevalenceValues()

  if (isLoading) {
    return <BenchmarkingContentRow graph={<Spin size="large" />} text={text} title={TITLE} />
  }

  if (error || !data) {
    return (
      <BenchmarkingContentRow
        title={TITLE}
        text={text}
        graph={
          <div className="flex h-full w-full items-center justify-center font-mono text-sm text-red-600">
            <span>{error ? `Fel vid hämtning av data: ${String(error)}` : 'Ingen data tillgänglig'}</span>
          </div>
        }
      />
    )
  }

  // Data är redan sorterat efter evaluation_count DESC från backend
  const labels = data.map((r) => r.task_name as string)
  const prevalences = data.map((r) => Number(r.prevalence_pct))
  const color = getColorByIndex(0)

  const datasets = [
    {
      label: 'Andel beräkningar (%)',
      data: prevalences,
      backgroundColor: color.fill,
      borderColor: color.border,
      borderWidth: BORDER_WIDTH,
    },
  ]

  const options: ComponentProps<typeof Bar>['options'] = {
    ...baseGraphOptions,
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.parsed.y}% av beräkningarna`,
        },
      },
    },
    scales: {
      x: {
        ticks: { font: { size: getFontSize('md') } },
      },
      y: {
        min: 0,
        max: 100,
        ticks: { font: { size: getFontSize('md') }, callback: (v) => `${v}%` },
        title: { display: true, text: 'Andel beräkningar (%)', font: { size: getFontSize('md') } },
      },
    },
  }

  return <BenchmarkingContentRow title={TITLE} text={text} graph={<Bar data={{ labels, datasets }} options={options} />} />
}
